import { useEffect, useState } from "react";
import { ThemeLayout } from "#/enum";
import ImpersonateBanner from "@/components/impersonate-banner";
import { LineLoading } from "@/components/loading";
import Logo from "@/components/logo";
import { down, useMediaQuery } from "@/hooks";
import { useSettings } from "@/store/settingStore";
import { useImpersonateState } from "@/store/userStore";
import { fileTokenLejaroban, getFileToken, refreshFileToken } from "@/utils/file-token";
import Header from "./header";
import Main from "./main";
import { NavHorizontalLayout, NavMobileLayout, NavVerticalLayout, useFilteredNavData } from "./nav";

export default function DashboardLayout() {
	const isMobile = useMediaQuery(down("md"));
	const impersonate = useImpersonateState();

	// A fájl-hozzáférési tokennek MEG KELL LENNIE, mielőtt bármit kirendelünk,
	// ami fájl-URL-t képez. A getStorageUrl() renderelés közben fut, és a
	// token aszinkron érkezik — enélkül az éppen bejelentkezett (de a
	// telepítés előtt belépett) felhasználónak üres tokennel képződött az
	// URL: `/files//price_quotes/...`, amire nem illeszkedik útvonal, és a
	// szerver `{"message":"Not Found"}`-ot adott. Így derült ki, hogy az
	// árajánlatok nem letölthetők.
	//
	// Ha már van token, azonnal rendelünk és háttérben újítunk; ha nincs,
	// megvárjuk. A `finally` azért kell, hogy hálózati hiba se tudja
	// örökre üres képernyőn tartani az alkalmazást.
	const [tokenKesz, setTokenKesz] = useState(() => Boolean(getFileToken()));

	// A token megváltozásakor újra kell rendelni, különben a már kirendelt
	// <img src> és letöltő hivatkozások a RÉGI tokennel maradnak az oldalon.
	const [, setTokenVerzio] = useState(0);

	useEffect(() => {
		let elo = true;

		const ujit = async () => {
			const elozo = getFileToken();
			await refreshFileToken();
			if (elo && getFileToken() !== elozo) {
				setTokenVerzio((v) => v + 1);
			}
		};

		ujit().finally(() => {
			if (elo) setTokenKesz(true);
		});

		// ⚠️ Időzítőre NEM lehet hagyatkozni: a Chrome felfüggeszti a háttérbe
		// tett lapok időzítőit, és alvó gépen sem futnak. Aki nyitva hagyta a
		// rendszert éjszakára, annak reggel lejárt tokenje volt, és a letöltés
		// 401-es JSON-t mentett le fájl helyett. Ezért az IGAZI horgony az,
		// hogy a lap előtérbe kerülésekor újítunk.
		const elotérbeKerult = () => {
			if (document.visibilityState === "visible" && fileTokenLejaroban()) {
				void ujit();
			}
		};

		document.addEventListener("visibilitychange", elotérbeKerult);
		window.addEventListener("focus", elotérbeKerult);

		// Biztonsági háló, ha a lap egész nap előtérben van.
		const ido = setInterval(
			() => {
				if (fileTokenLejaroban()) void ujit();
			},
			30 * 60 * 1000,
		);

		return () => {
			elo = false;
			clearInterval(ido);
			document.removeEventListener("visibilitychange", elotérbeKerult);
			window.removeEventListener("focus", elotérbeKerult);
		};
	}, []);

	if (!tokenKesz) {
		return <LineLoading />;
	}

	return (
		<div data-slot="slash-layout-root" className="w-full min-h-screen bg-background">
			<ImpersonateBanner />
			<div style={{ paddingTop: impersonate.isImpersonating ? "36px" : 0 }}>
				{isMobile ? <MobileLayout /> : <PcLayout />}
			</div>
		</div>
	);
}

function MobileLayout() {
	const navData = useFilteredNavData();
	return (
		<>
			{/* Sticky Header */}
			<Header leftSlot={<NavMobileLayout data={navData} />} />
			<Main />
		</>
	);
}

function PcLayout() {
	const { themeLayout } = useSettings();

	if (themeLayout === ThemeLayout.Horizontal) return <PcHorizontalLayout />;
	return <PcVerticalLayout />;
}

function PcHorizontalLayout() {
	const navData = useFilteredNavData();
	return (
		<>
			{/* Sticky Header */}
			<Header leftSlot={<Logo size={100} />} />
			{/* Sticky Nav */}
			<NavHorizontalLayout data={navData} />

			<Main />
		</>
	);
}

function PcVerticalLayout() {
	const settings = useSettings();
	const { themeLayout } = settings;
	const navData = useFilteredNavData();

	const mainPaddingLeft =
		themeLayout === ThemeLayout.Vertical ? "var(--layout-nav-width)" : "var(--layout-nav-width-mini)";

	return (
		<>
			{/* Fixed Header */}
			<NavVerticalLayout data={navData} />

			<div
				className="relative w-full min-h-screen flex flex-col transition-[padding] duration-300 ease-in-out"
				style={{
					paddingLeft: mainPaddingLeft,
				}}
			>
				<Header />
				<Main />
			</div>
		</>
	);
}
